import { useState, useRef, useCallback } from 'react'

export interface UseProgressOptions {
  speed?: number // milliseconds between increments
  onDone?: () => void // callback when progress reaches 100%
  linearProgress?: boolean // if true, progress increases at consistent rate instead of diminishing returns
  immediateComplete?: boolean // if true, completes immediately to 100% instead of waiting at 99%
}

export interface ProgressResult {
  start: () => void
  done: () => void
  progress: number
  error: boolean
  isRunning: boolean
  reset: () => void
}

export const useProgress = (options: UseProgressOptions = {}): ProgressResult => {
  const { speed = 100, onDone, linearProgress = false, immediateComplete = false } = options // default 100ms between increments

  const [progress, setProgress] = useState(0)
  const [error, setError] = useState(false)
  const [isRunning, setIsRunning] = useState(false)

  const intervalRef = useRef<NodeJS.Timeout | null>(null)
  const timeoutRef = useRef<NodeJS.Timeout | null>(null)
  const isDoneRef = useRef(false)
  const currentProgressRef = useRef(0)

  const clearTimers = useCallback(() => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current)
      intervalRef.current = null
    }
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current)
      timeoutRef.current = null
    }
  }, [])

  const start = useCallback(() => {
    // Reset state
    setProgress(0)
    setError(false)
    setIsRunning(true)
    isDoneRef.current = false
    currentProgressRef.current = 0
    clearTimers()

    intervalRef.current = setInterval(() => {
      if (isDoneRef.current) return

      // Increment progress - either linear or with diminishing returns
      const increment = linearProgress
        ? 0.5 // consistent increment for linear progress
        : Math.max(0.1, (99 - currentProgressRef.current) * 0.02) // diminishing returns

      // Progress normally, but handle 99% differently based on immediateComplete
      currentProgressRef.current = Math.min(99, currentProgressRef.current + increment)
      setProgress(Math.floor(currentProgressRef.current))

      // When reaching 99%, either complete immediately or start timeout
      if (currentProgressRef.current >= 99 && !timeoutRef.current) {
        if (immediateComplete) {
          // Complete immediately to 100% when reaching 99%
          currentProgressRef.current = 100
          setProgress(100)
          setIsRunning(false)
          clearTimers()
          onDone?.()
          return
        } else {
          // Start the 10-second timeout
          timeoutRef.current = setTimeout(() => {
            if (!isDoneRef.current) {
              setError(true)
              setIsRunning(false)
              clearTimers()
            }
          }, 10000) // 10 seconds
        }
      }
    }, speed)
  }, [speed, clearTimers, linearProgress, immediateComplete, onDone])

  const done = useCallback(() => {
    isDoneRef.current = true
    clearTimers()

    // Speed up to 100%
    let currentProgress = currentProgressRef.current
    const fastInterval = setInterval(() => {
      currentProgress += 5
      if (currentProgress >= 100) {
        currentProgress = 100
        currentProgressRef.current = 100
        setProgress(100)
        setIsRunning(false)
        clearInterval(fastInterval)
        onDone?.()
      } else {
        currentProgressRef.current = currentProgress
        setProgress(Math.floor(currentProgress))
      }
    }, 50) // Fast completion every 50ms
  }, [clearTimers, onDone])

  // Cleanup on unmount
  const reset = useCallback(() => {
    clearTimers()
    setProgress(0)
    setError(false)
    setIsRunning(false)
    isDoneRef.current = false
    currentProgressRef.current = 0
  }, [clearTimers])

  return {
    start,
    done,
    progress,
    error,
    isRunning,
    reset,
  } as ProgressResult
}
